import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BadgeCheck, CalendarDays, CalendarClock, Dumbbell, Handshake, Search, Trash2 } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { useModal } from '../../context/ModalContext';
import { toDateString } from '../../utils/eventDateFilters';
import { SPORT_CATEGORY } from '../../utils/eventCategories';
import { TAIWAN_CITIES } from '../../utils/taiwanCities';

const inputStyle = {
  boxSizing: 'border-box',
  width: '100%',
  minHeight: '44px',
  padding: '10px 12px',
  color: '#1e293b',
  background: '#ffffff',
  border: '1px solid #cbd5e1',
  borderRadius: '10px',
  fontFamily: 'inherit',
  fontSize: '14px'
};

const buttonStyle = {
  minHeight: '44px',
  padding: '10px 16px',
  borderRadius: '10px',
  fontFamily: 'inherit',
  fontSize: '14px',
  fontWeight: '700',
  cursor: 'pointer'
};

const toDateTimeLocal = value => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const getRecommendationStatus = recommendation => {
  if (!recommendation) return { label: '未推薦', color: '#64748b', background: '#f1f5f9' };
  const now = Date.now();
  if (new Date(recommendation.starts_at).getTime() > now) {
    return { label: '已排程', color: '#8a5a11', background: '#fff7e8' };
  }
  if (recommendation.ends_at && new Date(recommendation.ends_at).getTime() <= now) {
    return { label: '已到期', color: '#64748b', background: '#f1f5f9' };
  }
  return {
    label: recommendation.recommendation_type === 'partner' ? '合作中' : '推薦中',
    color: recommendation.recommendation_type === 'partner' ? '#f59e0b' : '#27649f',
    background: recommendation.recommendation_type === 'partner' ? '#fff4cc' : '#eef4f9'
  };
};

const getRecommendationFilterValue = event => {
  if (!event.isEligible) return 'invalid';
  if (!event.recommendation) return 'none';
  const now = Date.now();
  if (new Date(event.recommendation.starts_at).getTime() > now) return 'scheduled';
  if (event.recommendation.ends_at && new Date(event.recommendation.ends_at).getTime() <= now) return 'expired';
  return event.recommendation.recommendation_type;
};

const EMPTY_FILTERS = {
  event: { query: '', subtype: 'all', city: 'all', recommendation: 'all' },
  sport: { query: '', subtype: 'all', city: 'all', recommendation: 'all' }
};

const RECOMMENDATION_FILTER_OPTIONS = [
  { value: 'all', label: '全部推薦狀態' },
  { value: 'official', label: '官方推薦' },
  { value: 'partner', label: '品牌合作' },
  { value: 'scheduled', label: '已排程' },
  { value: 'expired', label: '已到期' },
  { value: 'none', label: '未推薦' },
  { value: 'invalid', label: '活動已失效' }
];

const RECOMMENDATION_TABS = [
  { value: 'event', label: '揪活動', icon: CalendarDays },
  { value: 'sport', label: '揪運動', icon: Dumbbell }
];

const createFormState = recommendation => ({
  recommendationType: recommendation?.recommendation_type || 'official',
  startsAt: toDateTimeLocal(recommendation?.starts_at),
  endsAt: recommendation?.ends_at ? toDateTimeLocal(recommendation.ends_at) : '',
  priority: String(recommendation?.priority ?? 0)
});

export default function AdminEventRecommendations() {
  const { showAlert, showConfirm } = useModal();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [form, setForm] = useState(createFormState());
  const [activeTab, setActiveTab] = useState('event');
  const [filtersByTab, setFiltersByTab] = useState(EMPTY_FILTERS);

  const loadEvents = useCallback(async () => {
    setIsLoading(true);
    try {
      const [eventsResult, recommendationsResult] = await Promise.all([
        supabase
          .from('events')
          .select('id, title, date, start_time, city, category, subcategory, max_participants, organizer_id, profiles:organizer_id(name), event_participants(status)')
          .eq('status', 'active')
          .gte('date', toDateString(new Date()))
          .order('date', { ascending: true }),
        supabase
          .from('event_recommendations')
          .select('event_id, recommendation_type, starts_at, ends_at, priority, updated_at, events:event_id(id, title, date, start_time, city, category, subcategory, max_participants, organizer_id, status, profiles:organizer_id(name), event_participants(status))')
          .order('priority', { ascending: false })
      ]);

      if (eventsResult.error) throw eventsResult.error;
      if (recommendationsResult.error) throw recommendationsResult.error;

      const recommendationMap = new Map((recommendationsResult.data || []).map(item => [item.event_id, item]));
      const activeEvents = (eventsResult.data || []).map(event => ({
        ...event,
        approvedCount: (event.event_participants || []).filter(participant => participant.status === 'approved').length,
        recommendation: recommendationMap.get(event.id) || null,
        isEligible: true
      }));
      const activeEventIds = new Set(activeEvents.map(event => event.id));
      const inactiveRecommendedEvents = (recommendationsResult.data || [])
        .filter(recommendation => recommendation.events && !activeEventIds.has(recommendation.event_id))
        .map(recommendation => ({
          ...recommendation.events,
          approvedCount: (recommendation.events.event_participants || []).filter(participant => participant.status === 'approved').length,
          recommendation,
          isEligible: false
        }));
      setEvents([...activeEvents, ...inactiveRecommendedEvents]);
    } catch (error) {
      console.error('載入活動推薦管理資料失敗:', error);
      showAlert('載入失敗', '無法取得活動與推薦設定，請稍後再試。');
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const activeFilters = filtersByTab[activeTab];
  const tabCounts = useMemo(() => ({
    event: events.filter(event => event.category !== SPORT_CATEGORY).length,
    sport: events.filter(event => event.category === SPORT_CATEGORY).length
  }), [events]);
  const subtypeOptions = useMemo(() => [...new Set(events
    .filter(event => activeTab === 'sport' ? event.category === SPORT_CATEGORY : event.category !== SPORT_CATEGORY)
    .map(event => activeTab === 'sport' ? event.subcategory : event.category)
    .filter(Boolean))].sort((first, second) => first.localeCompare(second, 'zh-TW')), [activeTab, events]);
  const filteredEvents = useMemo(() => {
    const keyword = activeFilters.query.trim().toLocaleLowerCase('zh-TW');
    return events.filter(event => {
      const searchable = [event.title, event.profiles?.name, event.category, event.subcategory, event.city]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('zh-TW');
      const isSport = event.category === SPORT_CATEGORY;
      const subtype = isSport ? event.subcategory : event.category;
      return (activeTab === 'sport' ? isSport : !isSport)
        && (!keyword || searchable.includes(keyword))
        && (activeFilters.subtype === 'all' || subtype === activeFilters.subtype)
        && (activeFilters.city === 'all' || event.city === activeFilters.city)
        && (activeFilters.recommendation === 'all' || getRecommendationFilterValue(event) === activeFilters.recommendation);
    });
  }, [activeFilters, activeTab, events]);

  const updateActiveFilter = (field, value) => {
    setFiltersByTab(current => ({
      ...current,
      [activeTab]: { ...current[activeTab], [field]: value }
    }));
  };

  const changeTab = tab => {
    setActiveTab(tab);
    setSelectedEvent(null);
  };

  const handleTabKeyDown = (keyboardEvent, currentTab) => {
    const currentIndex = RECOMMENDATION_TABS.findIndex(tab => tab.value === currentTab);
    let nextIndex = currentIndex;
    if (keyboardEvent.key === 'ArrowRight') nextIndex = (currentIndex + 1) % RECOMMENDATION_TABS.length;
    else if (keyboardEvent.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + RECOMMENDATION_TABS.length) % RECOMMENDATION_TABS.length;
    else if (keyboardEvent.key === 'Home') nextIndex = 0;
    else if (keyboardEvent.key === 'End') nextIndex = RECOMMENDATION_TABS.length - 1;
    else return;

    keyboardEvent.preventDefault();
    const nextTab = RECOMMENDATION_TABS[nextIndex].value;
    changeTab(nextTab);
    keyboardEvent.currentTarget.parentElement
      ?.querySelector(`#recommendation-${nextTab}-tab`)
      ?.focus();
  };

  const openEditor = event => {
    setSelectedEvent(event);
    setForm(createFormState(event.recommendation));
  };

  const handleSave = async submitEvent => {
    submitEvent.preventDefault();
    if (!selectedEvent) return;

    const startsAt = new Date(form.startsAt);
    const endsAt = form.endsAt ? new Date(form.endsAt) : null;
    const priority = Number(form.priority);
    if (!form.startsAt || Number.isNaN(startsAt.getTime())) {
      showAlert('欄位未完成', '請設定推薦開始時間。');
      return;
    }
    if (endsAt && (Number.isNaN(endsAt.getTime()) || endsAt <= startsAt)) {
      showAlert('時間設定錯誤', '推薦結束時間必須晚於開始時間。');
      return;
    }
    if (!Number.isInteger(priority) || priority < 0 || priority > 100) {
      showAlert('優先值錯誤', '優先值必須是 0 到 100 的整數。');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        event_id: selectedEvent.id,
        recommendation_type: form.recommendationType,
        starts_at: startsAt.toISOString(),
        ends_at: endsAt ? endsAt.toISOString() : null,
        priority
      };
      const { error } = await supabase
        .from('event_recommendations')
        .upsert(payload, { onConflict: 'event_id' });
      if (error) throw error;

      showAlert('儲存成功', form.recommendationType === 'partner' ? '已設定為品牌合作活動。' : '已設定為官方推薦活動。');
      setSelectedEvent(null);
      await loadEvents();
    } catch (error) {
      console.error('儲存活動推薦失敗:', error);
      showAlert('儲存失敗', '請確認管理員權限與推薦時間後再試。');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = event => {
    showConfirm('取消推薦', `確定要取消「${event.title}」的推薦設定嗎？`, async () => {
      try {
        const { error } = await supabase.from('event_recommendations').delete().eq('event_id', event.id);
        if (error) throw error;
        if (selectedEvent?.id === event.id) setSelectedEvent(null);
        showAlert('已取消推薦', '活動仍會保留在一般活動列表。');
        await loadEvents();
      } catch (error) {
        console.error('取消活動推薦失敗:', error);
        showAlert('取消失敗', '無法取消推薦，請稍後再試。');
      }
    });
  };

  return (
    <div style={{ display: 'grid', gap: '20px' }}>
      <section style={{ padding: '24px', background: '#ffffff', borderRadius: '16px', boxShadow: '0 4px 12px rgba(15,23,42,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '20px' }}>
          <BadgeCheck aria-hidden="true" size={26} color="#4e8bca" />
          <div>
            <h2 style={{ margin: '0 0 5px', color: '#1e293b', fontSize: '1.25rem' }}>活動推薦管理</h2>
            <p style={{ margin: 0, color: '#64748b', fontSize: '14px', lineHeight: 1.6 }}>管理官方推薦與品牌合作；近期熱門由最近 7 天有效報名自動計算，無法人工指定。</p>
          </div>
        </div>

        <div role="tablist" aria-label="推薦內容類型" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', maxWidth: '420px', marginBottom: '18px', padding: '4px', background: '#f1f5f9', borderRadius: '12px' }}>
          {RECOMMENDATION_TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                id={`recommendation-${tab.value}-tab`}
                aria-selected={isActive}
                aria-controls="recommendation-events-panel"
                tabIndex={isActive ? 0 : -1}
                onClick={() => changeTab(tab.value)}
                onKeyDown={event => handleTabKeyDown(event, tab.value)}
                style={{ minHeight: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '9px 14px', color: isActive ? '#27649f' : '#64748b', background: isActive ? '#ffffff' : 'transparent', border: isActive ? '1px solid #bdd7ee' : '1px solid transparent', borderRadius: '9px', boxShadow: isActive ? '0 2px 8px rgba(15,23,42,0.07)' : 'none', fontFamily: 'inherit', fontSize: '14px', fontWeight: '800', cursor: 'pointer' }}
              >
                <Icon aria-hidden="true" size={17} />
                {tab.label}
                <span aria-label={`${tabCounts[tab.value]} 筆`} style={{ minWidth: '22px', padding: '2px 6px', color: isActive ? '#27649f' : '#64748b', background: isActive ? '#eef4f9' : '#e2e8f0', borderRadius: '999px', fontSize: '11px' }}>{tabCounts[tab.value]}</span>
              </button>
            );
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))', gap: '10px' }}>
          <label style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>{activeTab === 'sport' ? '搜尋運動揪團' : '搜尋活動'}</span>
            <Search aria-hidden="true" size={18} color="#94a3b8" style={{ position: 'absolute', left: '13px', top: '13px' }} />
            <input value={activeFilters.query} onChange={event => updateActiveFilter('query', event.target.value)} placeholder={activeTab === 'sport' ? '搜尋運動、主辦人或城市' : '搜尋活動、主辦人或城市'} style={{ ...inputStyle, paddingLeft: '40px' }} />
          </label>
          <select aria-label={activeTab === 'sport' ? '篩選運動項目' : '篩選活動類別'} value={activeFilters.subtype} onChange={event => updateActiveFilter('subtype', event.target.value)} style={inputStyle}>
            <option value="all">{activeTab === 'sport' ? '全部運動項目' : '全部活動類別'}</option>
            {subtypeOptions.map(item => <option key={item} value={item}>{item}</option>)}
          </select>
          <select aria-label="篩選縣市" value={activeFilters.city} onChange={event => updateActiveFilter('city', event.target.value)} style={inputStyle}>
            <option value="all">全部縣市</option>
            {TAIWAN_CITIES.map(city => <option key={city} value={city}>{city}</option>)}
          </select>
          <select aria-label="篩選推薦狀態" value={activeFilters.recommendation} onChange={event => updateActiveFilter('recommendation', event.target.value)} style={inputStyle}>
            {RECOMMENDATION_FILTER_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
      </section>

      {selectedEvent && (
        <section aria-labelledby="recommendation-editor-title" style={{ padding: '24px', background: '#f8fbfe', border: '1px solid #bdd7ee', borderRadius: '16px' }}>
          <h3 id="recommendation-editor-title" style={{ margin: '0 0 4px', color: '#1e293b', fontSize: '17px' }}>設定推薦：{selectedEvent.title}</h3>
          <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: '13px' }}>若有費用、抽成、交換曝光或其他利益關係，必須選擇「品牌合作」。</p>
          <form onSubmit={handleSave} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', alignItems: 'end' }}>
            <label style={{ display: 'grid', gap: '6px', color: '#334155', fontSize: '13px', fontWeight: '700' }}>
              推薦類型
              <select value={form.recommendationType} onChange={event => setForm(current => ({ ...current, recommendationType: event.target.value }))} style={inputStyle}>
                <option value="official">官方推薦</option>
                <option value="partner">品牌合作</option>
              </select>
            </label>
            <label style={{ display: 'grid', gap: '6px', color: '#334155', fontSize: '13px', fontWeight: '700' }}>
              開始時間
              <input type="datetime-local" required value={form.startsAt} onChange={event => setForm(current => ({ ...current, startsAt: event.target.value }))} style={inputStyle} />
            </label>
            <label style={{ display: 'grid', gap: '6px', color: '#334155', fontSize: '13px', fontWeight: '700' }}>
              結束時間（可留空）
              <input type="datetime-local" value={form.endsAt} min={form.startsAt || undefined} onChange={event => setForm(current => ({ ...current, endsAt: event.target.value }))} style={inputStyle} />
            </label>
            <label style={{ display: 'grid', gap: '6px', color: '#334155', fontSize: '13px', fontWeight: '700' }}>
              優先值（0–100）
              <input type="number" min="0" max="100" step="1" value={form.priority} onChange={event => setForm(current => ({ ...current, priority: event.target.value }))} style={inputStyle} />
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <button type="submit" disabled={isSaving} style={{ ...buttonStyle, color: '#ffffff', background: '#4e8bca', border: 'none', opacity: isSaving ? 0.65 : 1 }}>{isSaving ? '儲存中…' : '儲存推薦'}</button>
              <button type="button" disabled={isSaving} onClick={() => setSelectedEvent(null)} style={{ ...buttonStyle, color: '#475569', background: '#ffffff', border: '1px solid #cbd5e1' }}>取消編輯</button>
            </div>
          </form>
        </section>
      )}

      <section id="recommendation-events-panel" role="tabpanel" aria-labelledby={`recommendation-${activeTab}-tab`} aria-label={`可設定推薦的${activeTab === 'sport' ? '運動揪團' : '活動'}`} style={{ display: 'grid', gap: '10px' }}>
        {isLoading && <div role="status" style={{ padding: '36px', color: '#64748b', background: '#ffffff', borderRadius: '16px', textAlign: 'center' }}>載入活動中…</div>}
        {!isLoading && filteredEvents.length === 0 && <div style={{ padding: '36px', color: '#64748b', background: '#ffffff', borderRadius: '16px', textAlign: 'center' }}>沒有符合條件且可推薦的{activeTab === 'sport' ? '運動揪團' : '活動'}。</div>}
        {!isLoading && filteredEvents.map(event => {
          const recommendationStatus = event.isEligible
            ? getRecommendationStatus(event.recommendation)
            : { label: '活動已失效', color: '#b42318', background: '#fff1f0' };
          return (
            <article key={event.id} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '14px', padding: '16px 18px', background: '#ffffff', border: selectedEvent?.id === event.id ? '1px solid #7fb0dc' : '1px solid #e2e8f0', borderRadius: '14px', boxShadow: '0 3px 10px rgba(15,23,42,0.035)' }}>
              <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '7px', marginBottom: '7px' }}>
                  <strong style={{ color: '#1e293b', fontSize: '15px' }}>{event.title}</strong>
                  <span style={{ padding: '3px 8px', color: recommendationStatus.color, background: recommendationStatus.background, borderRadius: '999px', fontSize: '11px', fontWeight: '800' }}>{recommendationStatus.label}</span>
                  {event.recommendation?.recommendation_type === 'partner' && <Handshake aria-label="品牌合作" size={16} color="#f59e0b" />}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', color: '#64748b', fontSize: '12px', lineHeight: 1.5 }}>
                  <span>{event.date} {event.start_time?.slice(0, 5)}</span>
                  <span>{event.category === SPORT_CATEGORY ? (event.subcategory || '運動項目未設定') : event.category}</span>
                  {event.city && <span>{event.city}</span>}
                  <span>主辦：{event.profiles?.name || '未提供'}</span>
                  <span>已核准 {event.approvedCount} 人</span>
                  {event.recommendation && <span>優先值 {event.recommendation.priority}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {event.isEligible && (
                  <button type="button" onClick={() => openEditor(event)} style={{ ...buttonStyle, display: 'inline-flex', alignItems: 'center', gap: '7px', color: '#27649f', background: '#eef4f9', border: '1px solid #bdd7ee' }}>
                    <CalendarClock aria-hidden="true" size={17} />
                    {event.recommendation ? '調整推薦' : '設定推薦'}
                  </button>
                )}
                {event.recommendation && (
                  <button type="button" aria-label={`取消 ${event.title} 的推薦`} onClick={() => handleRemove(event)} style={{ ...buttonStyle, display: 'inline-flex', alignItems: 'center', gap: '7px', color: '#b42318', background: '#fff7f6', border: '1px solid #fecaca' }}>
                    <Trash2 aria-hidden="true" size={17} />取消推薦
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
}
